import React, { memo, useMemo, useRef } from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Product, ProductVariant } from "@shopify/hydrogen-react/storefront-api-types"

import { useCore } from "@app/hooks/useCore"
import { useFindInStore } from "@app/hooks/useFindInStore"
import Icon from "@app/components/Icon"
import { ProductStockOverlayFormDetails } from "@app/components/Product/Stock/ProductStockOverlayFormDetails"
import { ProductStockOverlayFormProduct } from "@app/components/Product/Stock/ProductStockOverlayFormProduct"

type Props = {
  activeStore: string | null
  disableNoActiveOption: (state: boolean) => void
  handleActive: (id: string | null) => void
  handleBackInStock: () => void
  handleQuantity: (quantity: number) => void
  handleToggle: () => void
  isUnavailable: boolean
  loading: boolean
  noActiveOption: boolean
  parentProduct: Product
  product: Product
  quantity: number
  selectedVariant: ProductVariant
  setSelectedSiblingHandle: (handle: string) => void
  siblings: Array<any>
}

export const ProductStockOverlayForm: React.FC<Props> = memo(
  ({
    activeStore,
    disableNoActiveOption,
    handleActive,
    handleBackInStock,
    handleQuantity,
    handleToggle,
    isUnavailable,
    loading,
    noActiveOption,
    parentProduct,
    product,
    quantity,
    selectedVariant,
    setSelectedSiblingHandle,
    siblings,
  }) => {
    const { product: locales } = useStaticQuery<GatsbyTypes.ProductStockOverlayFormQuery>(graphql`
      query ProductStockOverlayForm {
        product: sanityTemplateProduct {
          additionalStock
          additionalStockLocate
          additionalStockSubmit
          additionalStockEmpty
          additionalStockIn
          additionalStockLow
          additionalStockCheck
          additionalStockOut
          additionalStockShow
          additionalStockPlaceholder
          additionalStockResults
          additionalCollection
          additionalCollectionUnavailable
        }
      }
    `)
    const {
      helpers: { capitalise, linebreak },
    } = useCore()
    const {
      errors,
      handleChange,
      handleGeolocate,
      handleOption,
      handleSubmit,
      handleShowAll,
      hasMore,
      isLoading,
      position,
      search,
      searched,
      selectedOptions,
      settings,
      stores,
      variant,
    } = useFindInStore(product, selectedVariant)

    const hasResults = useMemo(() => position && stores.length > 0 && !isLoading, [isLoading, position, stores?.length])

    const isEmpty = useMemo(() => position && !(stores.length > 0) && !isLoading, [isLoading, position, stores?.length])
    const activeStoreDetails = useMemo(() => activeStore !== null && stores.find(({ key }) => key === activeStore), [activeStore, stores])
    const inputRef = useRef(null)

    return (
      <>
        <HStack
          transform={{ base: activeStoreDetails ? "translateX(-50%)" : "translateX(0%)", lg: "unset" }}
          alignItems="stretch"
          justifyContent="space-between"
          w={{ base: "200vw", lg: "84rem" }}
          h="full"
          overflow="hidden"
          spacing={0}
          transition={{ base: "transform 0.2s ease", lg: "none" }}
        >
          <Box p={5} color="black" w={{ base: "100vw", lg: "42rem" }} overflowX="hidden" overflowY="auto" h="full">
            <Flex alignItems="center" justifyContent="space-between" mb={6}>
              <IconButton
                onClick={handleToggle}
                title={global?.additionalClose || ""}
                aria-label={global?.additionalClose || ""}
                icon={<Icon name="chevronLeft" width={3} />}
                cursor="pointer"
              />
              <Heading as="p" variant="h3" textAlign="center">
                {locales?.additionalStock}
              </Heading>
              <Box w={3} />
            </Flex>
            <Box
              as="form"
              onSubmit={e => {
                handleSubmit(e)
                handleActive(null)
                if (inputRef.current) inputRef.current.blur()
              }}
              alignItems="stretch"
              justifyContent="flex-start"
              flexShrink={0}
              action="."
            >
              <InputGroup>
                <Input
                  autoComplete="postal-code"
                  borderColor="black"
                  borderRadius="40px !important"
                  fontSize="16px"
                  isDisabled={isLoading}
                  onChange={handleChange}
                  name="query"
                  placeholder={locales?.additionalStockPlaceholder || ""}
                  value={search}
                  _hover={{ borderColor: "black" }}
                  pr="100px"
                  type="text"
                  ref={inputRef}
                />
                <InputRightAddon pos="absolute" insetY={0} right={6} bg="transparent" border="none" zIndex={1}>
                  <HStack alignItems="stretch" justifyContent="flex-end" spacing={0} h="full">
                    <IconButton
                      isDisabled={isLoading}
                      onClick={handleGeolocate}
                      title={locales?.additionalStockLocate || ""}
                      type="button"
                      aria-label={locales?.additionalStockLocate || ""}
                      icon={<Icon name="crosshair" width={5} height={5} />}
                      px="8px !important"
                    />
                    <IconButton
                      isDisabled={isLoading}
                      title={locales?.additionalStockSubmit || ""}
                      type="submit"
                      aria-label={locales?.additionalStockSubmit || ""}
                      icon={<Icon name="search" width={5} height={5} />}
                      pl="8px !important"
                      pr="24px !important"
                      mr="-24px !important"
                    />
                  </HStack>
                </InputRightAddon>
              </InputGroup>
              {errors?.length > 0 ? (
                <>
                  {errors?.map(error => (
                    <Flex key={error?.code || error?.toString()} alignItems="center" color="red" justifyContent="flex-start">
                      <Text variant="text12" mt={2} ml={5}>
                        {error?.message || error?.toString()}
                      </Text>
                    </Flex>
                  ))}
                </>
              ) : null}
            </Box>

            <ProductStockOverlayFormProduct
              disableNoActiveOption={disableNoActiveOption}
              handleBackInStock={handleBackInStock}
              handleOption={handleOption}
              handleQuantity={handleQuantity}
              handleToggle={handleToggle}
              isUnavailable={isUnavailable}
              loading={loading}
              noActiveOption={noActiveOption}
              parentProduct={parentProduct}
              product={product}
              quantity={quantity}
              selectedOptions={selectedOptions}
              setSelectedSiblingHandle={setSelectedSiblingHandle}
              siblings={siblings}
              variant={variant}
            />

            {hasResults || isEmpty ? (
              <>
                <Divider mt={"26px"} />
                {searched && (
                  <Text alignSelf="center" textAlign="center" variant="base" lineHeight={1.25} mt={"26px"} fontSize="0.9375rem">
                    <Box as="span" color="brand.grayText">
                      {locales?.additionalStockResults} &apos;
                    </Box>
                    {searched?.split(",")?.[0] || searched}
                    <Box as="span" color="brand.grayText">
                      &apos;
                    </Box>
                  </Text>
                )}
                {hasResults ? (
                  <VStack
                    alignItems="stretch"
                    justifyContent="flex-start"
                    pt="26px"
                    spacing={8}
                    pb={{ base: hasMore ? 0 : 5, md: 5 }}
                    divider={<Divider />}
                  >
                    {stores.map(({ key, addressCity, addressState, enableClickAndCollect, stock, title }) => (
                      <Box key={key}>
                        <VStack alignItems="stretch" justifyContent="flex-start" spacing={3}>
                          <HStack alignItems="flex-start" justifyContent="space-between">
                            <VStack alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                              <Text variant="text18" my={0}>
                                {title}
                              </Text>
                              <Text variant="paragraph14" my={0}>
                                {[capitalise((addressCity || "")?.toLowerCase()), addressState].join(", ")}
                              </Text>
                            </VStack>
                            <VStack alignItems="flex-end" textAlign="right" justifyContent="flex-start" spacing={1}>
                              <Text
                                color={
                                  stock > settings?.lowStockThreshold
                                    ? "brand.green"
                                    : stock > settings?.noStockThreshold
                                      ? "brand.orange"
                                      : "brand.red"
                                }
                                my={0}
                                variant="text14"
                              >
                                {stock > settings?.lowStockThreshold
                                  ? locales?.additionalStockIn
                                  : stock > settings?.noStockThreshold
                                    ? locales?.additionalStockLow
                                    : locales?.additionalStockOut}
                              </Text>
                              {!(stock > settings?.lowStockThreshold) && stock > settings?.noStockThreshold && (
                                <Text color="brand.orange" variant="paragraph12">
                                  {locales?.additionalStockCheck}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                          <HStack alignItems="flex-start" justifyContent="space-between">
                            <Text
                              as={Button}
                              onClick={() => handleActive(key)}
                              variant="text14"
                              my={0}
                              fontWeight={500}
                              textDecoration="underline"
                            >
                              {locales?.additionalStockShow}
                            </Text>
                            {enableClickAndCollect && (
                              <Text
                                variant="text14"
                                textAlign="right"
                                my={0}
                                color={stock <= settings?.lowStockThreshold ? "brand.grayText" : undefined}
                              >
                                {stock > settings?.lowStockThreshold
                                  ? locales?.additionalCollection
                                  : locales?.additionalCollectionUnavailable}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                    {hasMore && (
                      <Button onClick={handleShowAll} title="View More" aria-label="View More" py={6} mt={-8} mb={-8}>
                        <Icon name="chevronDownDropdown" height={2.5} width={3} />
                        <Text ml={4} size="text14">
                          View More
                        </Text>
                      </Button>
                    )}
                  </VStack>
                ) : (
                  <VStack alignItems="center" justifyContent="center" flex={1} pt="35px">
                    <Text
                      alignSelf="center"
                      textAlign="center"
                      variant="base"
                      lineHeight={1.25}
                      dangerouslySetInnerHTML={{ __html: linebreak(locales?.additionalStockEmpty || "") }}
                    />
                  </VStack>
                )}
              </>
            ) : (
              isLoading && (
                <VStack alignItems="center" justifyContent="center" flex={1} pt="35px">
                  <Spinner />
                </VStack>
              )
            )}
          </Box>
          <VStack
            alignItems="stretch"
            justifyContent="space-between"
            bg="brand.darkBlue"
            color="white"
            w={{ base: "100vw", lg: "42rem" }}
            overflowX="hidden"
            overflowY="auto"
            h="full"
          >
            <Flex alignItems="flex-start" justifyContent="flex-end" px={5} py={4} flexShrink={0}>
              <IconButton
                onClick={() => handleActive(null)}
                title={global?.additionalClose || ""}
                aria-label={global?.additionalClose || ""}
                icon={<Icon name="close" width={8} height={8} />}
                cursor="pointer"
              />
            </Flex>
            <Box as={VStack} alignItems="stretch" justifyContent="flex-start" spacing={0} px={0} py={0} flex={1}>
              <ProductStockOverlayFormDetails key={activeStoreDetails?.key} store={activeStoreDetails} />
            </Box>
          </VStack>
        </HStack>
      </>
    )
  }
)
